import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography, CircularProgress, Paper, IconButton, Checkbox, FormGroup, FormControlLabel, Accordion, AccordionSummary, AccordionDetails, ListItem, Chip } from '@mui/material';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentOrder, type Order as ReduxOrder, type OrderStatus, type PaymentStatus } from '../store/slices/orderSlice';
import { setPaymentError } from '../store/slices/uiSlice';
import api from '../services/api';
import { PayNowButton } from '../components/PayNowButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import type { RootState, AppDispatch } from '../store';
import type { OrderItemStatus } from '../types/menu';
import type { User as AppUser } from '../store/slices/userSlice';
import type { PaymentError, PaymentErrorNotification } from '../types/payment';
import ReadyToPaySkeleton from '../components/skeletons/ReadyToPaySkeleton';
import {
  trackPageView,
  trackWithUserContext,
  trackBillReview,
  trackPaymentButtonClick,
  incrementTablePayment,
  getRestaurantId,
  setRestaurantId
} from '../utils/analytics';

interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  paidAmount?: number;
  imageUrl?: string;
  notes?: string;
  options?: Record<string, { name: string; price: number }[]>;
  menuItemId: string;
  displayOptions?: string[];
  userId?: string;
  userName?: string;
  status?: OrderItemStatus;
  user?: AppUser;
}

interface Order extends ReduxOrder {
  items: OrderItem[];
}

interface GroupedItems {
  [userId: string]: {
    userName: string;
    items: OrderItem[];
    total: number;
  };
}

interface MenuItem {
  id: string;
  name: string;
  imageUrl?: string;
}

const getStatusChipColor = (status: OrderItemStatus): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'preparing':
      return 'primary';
    case 'served':
      return 'success';
    case 'paid':
      return 'secondary';
    case 'failed_payment':
      return 'error';
    case 'added_to_cart':
    default:
      return 'default';
  }
};

const getStatusLabel = (status: OrderItemStatus): string => {
  switch (status) {
    case 'preparing':
      return 'Preparando';
    case 'served':
      return 'Servido';
    case 'paid':
      return 'Pagado';
    case 'failed_payment':
      return 'Pago Fallido';
    case 'added_to_cart':
      return 'En Carrito';
    default:
      return status.replace(/_/g, ' ');
  }
};

const processOrder = (order: Order): Order => {
  const processedItems = (order.items as OrderItem[]).map((item) => {
    const currentDisplayOptions: string[] = [];

    if (item.options && Object.keys(item.options).length > 0) {
      Object.values(item.options).forEach((optionGroup) => {
        if (Array.isArray(optionGroup)) {
          optionGroup.forEach((choice) => {
            if (choice && typeof choice.name === 'string') {
              currentDisplayOptions.push(choice.name);
            }
          });
        }
      });
    }

    if (item.notes && item.notes.trim() !== "") {
      currentDisplayOptions.push(item.notes);
    }

    return {
      ...item,
      displayOptions: currentDisplayOptions,
      userId: item.user?.id,
      userName: item.user?.name,
    };
  });

  // Sort processedItems alphabetically by name
  processedItems.sort((a, b) => a.name.localeCompare(b.name));
  
  return { ...order, items: processedItems };
};


const ReadyToPay = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [order, setOrder] = useState<Order | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payNowLoading, setPayNowLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const footerRef = useRef<HTMLDivElement>(null);
  const [expandedUser, setExpandedUser] = useState<string | false>(false);
  const currentUserId = useSelector((state: RootState) => state.user.currentUser?.id);
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const currentPaymentError = useSelector((state: RootState) => state.ui.paymentError);
  
  // WebSocket connection state
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [restaurantId, setRestaurantIdState] = useState<string | undefined>(undefined);

  // Removed paymentErrorModal local state - now handled by Redux
  
  const fetchOrderAndMenu = useCallback(async () => {
    if (!orderId) {
      setError('Order ID is missing.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const orderRes = await api.get<Order>(`/orders/${orderId}`);
      const fetchedOrderData: Order = orderRes.data;

      // Validate session by calling a session-protected endpoint
      if (fetchedOrderData.table?.id) {
        try {
          await api.get(`/tables/${fetchedOrderData.table.id}/current-order`);
        } catch (sessionError: any) {
          if (sessionError.response?.status === 403 && sessionError.response?.data?.code === 'SESSION_INVALID') {
            return;
          }
          console.warn('Session validation failed with non-session error:', sessionError);
        }
      }

      dispatch(setCurrentOrder(fetchedOrderData));

      const menuRes = await api.get<MenuItem[]>('/menu/items');
      const fetchedMenuItems: MenuItem[] = menuRes.data;

      const processedOrder = processOrder(fetchedOrderData);

      const itemsWithImages = processedOrder.items.map(item => {
        const menuItemDetail = fetchedMenuItems.find((mi: MenuItem) => mi.id === item.menuItemId);
        return {
          ...item,
          imageUrl: item.imageUrl || menuItemDetail?.imageUrl,
        };
      });

      setOrder({ ...processedOrder, items: itemsWithImages });
      setMenuItems(fetchedMenuItems);

      // Extract and store restaurant ID
      const extractedRestaurantId = getRestaurantId(fetchedOrderData);
      setRestaurantIdState(extractedRestaurantId);
      if (extractedRestaurantId) {
        setRestaurantId(extractedRestaurantId);
      }

      // Track page view and bill review (this is where users actually review their bill)
      trackPageView(window.location.href, `Ready to Pay - Order ${orderId}`, extractedRestaurantId);
      
      // Count items by user type for social payment insights
      const myItems = processedOrder.items?.filter((item: any) => item.user?.id === currentUserId) || [];
      const othersItems = processedOrder.items?.filter((item: any) => item.user?.id !== currentUserId) || [];
      
      trackBillReview(
        orderId || 'unknown',
        processedOrder.total || 0,
        myItems.length,
        othersItems.length,
        currentUser,
        fetchedOrderData.table?.id,
        extractedRestaurantId
      );

      if (isInitialLoad) {
        let initialSelected: string[] = [];
        if (currentUserId) {
          initialSelected = processedOrder.items
            .filter(item => item.userId === currentUserId && item.status !== 'added_to_cart')
            .map(item => item.id);
        }
        if (initialSelected.length === 0) {
          initialSelected = processedOrder.items
            .filter(item => (!item.userId || item.userId === 'guest') && item.status !== 'added_to_cart')
            .map(item => item.id);
        }
        setSelectedItems(new Set(initialSelected));

        if (currentUserId && processedOrder.items.some(item => item.userId === currentUserId)) {
          setExpandedUser(currentUserId);
        }
        
        setIsInitialLoad(false);
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('No se pudieron cargar los detalles de la orden o los artículos del menú.');
    } finally {
      setLoading(false);
    }
  }, [orderId, dispatch, currentUserId, isInitialLoad]);

  const connectWebSocket = useCallback(() => {
    if (!orderId) return;

    if (wsRef.current) {
      wsRef.current.close();
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const cleanBaseUrl = baseUrl.replace(/\/api\/?$/, '');
    const wsUrl = cleanBaseUrl.replace(/^https?:\/\//, '') + `/api/ws/orders/${orderId}`;
    const fullWsUrl = `${wsProtocol}://${wsUrl}`;

    console.log('Connecting to WebSocket:', fullWsUrl);

    try {
      wsRef.current = new WebSocket(fullWsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected successfully');
        setWsConnected(true);
      };

      wsRef.current.onmessage = (event) => {
        console.log('WebSocket message received:', event.data);
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ORDER_UPDATED' && data.order) {
            console.log('Order updated via WebSocket, updating state directly.');
            const newOrder = data.order as Order;
            
            const processedOrder = processOrder(newOrder);
            setOrder(processedOrder);

            setSelectedItems(prevSelected => {
              const newSelected = new Set<string>();
              const newItemIds = new Set(newOrder.items.map(item => item.id));
              for (const selectedId of prevSelected) {
                if (newItemIds.has(selectedId)) {
                  newSelected.add(selectedId);
                }
              }
              return newSelected;
            });
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        setWsConnected(false);
        
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect WebSocket...');
            connectWebSocket();
          }, 3000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      setWsConnected(false);
    }
  }, [orderId]);

  const disconnectWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounting');
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setWsConnected(false);
  }, []);

  useEffect(() => {
    fetchOrderAndMenu();
  }, [fetchOrderAndMenu]);

  useEffect(() => {
    connectWebSocket();
    return () => {
      disconnectWebSocket();
    };
  }, [connectWebSocket, disconnectWebSocket]);

  // Check for payment error parameters and show error modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentErrorType = searchParams.get('paymentError');
    
    if (paymentErrorType === 'sync' && order && currentPaymentError) {
      // Handle sync error from Redux state (PaymentCallback dispatch)
      // Enhance the error with user and table context if not already enhanced
      if (currentPaymentError.paymentId !== 'enhanced') {
        const enhancedError: PaymentErrorNotification = {
          ...currentPaymentError,
          paymentId: 'enhanced', // Mark as enhanced to avoid re-processing
          userId: currentUser?.id || currentPaymentError.userId,
          userName: currentUser?.name || currentPaymentError.userName,
          tableNumber: order.table?.number?.toString() || currentPaymentError.tableNumber,
          orderId: order.id
        };
        
        dispatch(setPaymentError(enhancedError));
      }
      
      // Clean up the URL parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location.search, dispatch, order, currentUser, currentPaymentError]);

  // Removed handleClosePaymentErrorModal - now handled by Redux actions

  // Moved items and total declaration before useEffect that uses items
  const items: OrderItem[] = Array.isArray(order?.items) ? order.items : [];
  const total = typeof order?.total === 'number' ? order.total : items.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  // Group items by user
  const groupedItems: GroupedItems = items.reduce((acc, item) => {
    const userId = item.userId || 'guest';
    // Determine if this is an admin/staff/manager/owner
    let userName = item.userName || 'Guest User';
    let userRole = (item.user && (item.user as any).role) || undefined;
    if (userRole && ['staff', 'manager', 'owner'].includes(userRole)) {
      userName = 'Mesero';
    }

    if (!acc[userId]) {
      acc[userId] = {
        userName,
        items: [],
        total: 0
      };
    }

    acc[userId].items.push(item);
    acc[userId].total += item.price * item.quantity;

    return acc;
  }, {} as GroupedItems);

  // Calculate total for selected items (only unpaid amounts)
  const selectedTotal = items
    .filter(item => selectedItems.has(item.id))
    .reduce((sum, item) => {
      const itemTotal = item.price * item.quantity;
      const paidAmount = Number(item.paidAmount) || 0;
      const remainingAmount = Math.max(0, itemTotal - paidAmount);
      return sum + remainingAmount;
    }, 0);

  const itemsToPay = items
    .filter(item => selectedItems.has(item.id))
    .filter(item => {
      // Only include items that aren't fully paid
      const itemTotal = item.price * item.quantity;
      const paidAmount = Number(item.paidAmount) || 0;
      return paidAmount < itemTotal;
    })
    .map(item => ({
      orderItemId: item.id,
    }));

  const handleItemSelection = (itemId: string) => {
    // Don't allow selection of fully paid items
    const item = items.find(i => i.id === itemId);
    if (item) {
      // Don't allow selection of cart items
      if (item.status === 'added_to_cart') {
        return;
      }
      
      const itemTotal = item.price * item.quantity;
      const paidAmount = Number(item.paidAmount) || 0;
      if (paidAmount >= itemTotal) {
        return; // Item is fully paid, don't allow selection
      }
    }

    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(itemId)) {
        newSelected.delete(itemId);
      } else {
        newSelected.add(itemId);
      }
      return newSelected;
    });
  };

  const handleSelectAllForUser = (userId: string) => {
    const userItems = groupedItems[userId].items.filter(item => {
      // Don't allow selection of cart items
      if (item.status === 'added_to_cart') {
        return false;
      }
      
      // Only allow selection of items that aren't fully paid
      const itemTotal = item.price * item.quantity;
      const paidAmount = Number(item.paidAmount) || 0;
      return paidAmount < itemTotal;
    });
    
    const allSelected = userItems.every(item => selectedItems.has(item.id));

    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      userItems.forEach(item => {
        if (allSelected) {
          newSelected.delete(item.id);
        } else {
          newSelected.add(item.id);
        }
      });
      return newSelected;
    });
  };

  const handleAccordionChange = (userId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedUser(isExpanded ? userId : false);
  };

  // Sort user groups: current user first, then others
  const sortedGroupedEntries = Object.entries(groupedItems).sort(([userIdA], [userIdB]) => {
    if (currentUserId && userIdA === currentUserId) return -1;
    if (currentUserId && userIdB === currentUserId) return 1;
    return 0;
  });

  if (loading) return <ReadyToPaySkeleton />;

  if (error || !order) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 2, background: '#f0f2f5' }}>
      <Typography variant="h6" color="error" textAlign="center">
        {error || 'Orden no encontrada.'}
      </Typography>
    </Box>
  );

  // console.log('Order object:', JSON.stringify(order, null, 2)); // Ensure this is removed or commented

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      minHeight: '100dvh',
      width: '100%',
      bgcolor: 'background.default',
      boxSizing: 'border-box',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      // Enhanced mobile viewport handling
      '@media screen and (max-width: 600px)': {
        height: '100dvh',
        minHeight: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
      }
    }}>

      <Box sx={{
        py: 1, px: 2,
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        bgcolor: '#ffffff',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>        
        <Typography component="div" sx={{ flexGrow: 1, textAlign: 'center', color: '#333', ml: '-40px', fontSize: '1.5rem', fontWeight: 600 }}>
          Tu Orden
        </Typography>
      </Box>

      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        width: '100%',
        maxWidth: '600px',
        mx: 'auto',
        bgcolor: '#ffffff',
        boxSizing: 'border-box',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        paddingBottom: '130px',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#cccccc', borderRadius: '3px' },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      }}>        
        <Box sx={{
          width: '100%',
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
          mb: '120px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '2px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }}>
          {sortedGroupedEntries.map(([userId, group]) => {
            // Group items by their characteristics (name, options, notes, and payment status)
            const groupedByCharacteristics = group.items.reduce((acc, item) => {
              // Include payment status in the grouping key to separate paid/unpaid items
              const itemTotal = item.price * item.quantity;
              const paidAmount = Number(item.paidAmount) || 0;
              const paymentStatus = paidAmount >= itemTotal ? 'paid' : paidAmount > 0 ? 'partial' : 'unpaid';
              
              const key = `${item.menuItemId}-${JSON.stringify(item.options)}-${item.notes || ''}-${paymentStatus}`;
              if (!acc[key]) {
                // Initialize a new group
                acc[key] = {
                  ...item,
                  quantity: 0, // Reset quantity to be summed up
                  ids: [],       // To store original item IDs for selection
                };
              }
              acc[key].quantity += item.quantity;
              acc[key].ids.push(item.id);
              return acc;
            }, {} as Record<string, OrderItem & { ids: string[] }>);

            const displayedItems = Object.values(groupedByCharacteristics);

            return (
              <Accordion
                key={userId}
                data-testid={`user-accordion-${group.userName}`}
                expanded={expandedUser === userId}
                onChange={handleAccordionChange(userId)}
                sx={{ mb: 2, boxShadow: 'none', border: '1px solid #f0f0f0', borderRadius: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel-${userId}-content`}
                  id={`panel-${userId}-header`}
                  sx={{ bgcolor: '#f8f9fa', px: 2, py: 1.5, borderRadius: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(() => {
                            // Check if all items are either selected or fully paid
                            return group.items.every(item => {
                              const itemTotal = item.price * item.quantity;
                              const paidAmount = Number(item.paidAmount) || 0;
                              return selectedItems.has(item.id) || paidAmount >= itemTotal;
                            });
                          })()}
                          indeterminate={(() => {
                            // Show indeterminate if some (but not all) items are selected or paid
                            const allChecked = group.items.every(item => {
                              const itemTotal = item.price * item.quantity;
                              const paidAmount = Number(item.paidAmount) || 0;
                              return selectedItems.has(item.id) || paidAmount >= itemTotal;
                            });
                            const someChecked = group.items.some(item => {
                              const itemTotal = item.price * item.quantity;
                              const paidAmount = Number(item.paidAmount) || 0;
                              return selectedItems.has(item.id) || paidAmount >= itemTotal;
                            });
                            return someChecked && !allChecked;
                          })()}
                          onChange={() => handleSelectAllForUser(userId)}
                          onClick={e => e.stopPropagation()}
                        />
                      }
                      label=""
                      sx={{ m: 0, mr: 1 }}
                    />
                    <PersonIcon sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle' }} />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
                      {group.userName}
                      {group.items.some(item => item.status === 'added_to_cart') && (
                        <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.5 }}>
                          (ordenando)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 2 }}>
                      ₡{group.total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
                  {displayedItems.map((item) => {
                    const allSubItemsSelected = item.ids.every(id => selectedItems.has(id));
                    const someSubItemsSelected = !allSubItemsSelected && item.ids.some(id => selectedItems.has(id));
                    
                    return (
                      <Box
                        key={item.id} // Using the first item's ID of the group as key
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          px: 2,
                          py: 1.5,
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <Checkbox
                          checked={allSubItemsSelected || (() => {
                            // Also check if all items in this group are fully paid
                            const itemsInGroup = group.items.filter(groupItem => item.ids.includes(groupItem.id));
                            return itemsInGroup.every(groupItem => {
                              const itemTotal = Number(groupItem.price) * Number(groupItem.quantity);
                              const paidAmount = Number(groupItem.paidAmount) || 0;
                              return paidAmount >= itemTotal;
                            });
                          })()}
                          indeterminate={someSubItemsSelected && !(() => {
                            // Don't show indeterminate if all items are fully paid
                            const itemsInGroup = group.items.filter(groupItem => item.ids.includes(groupItem.id));
                            return itemsInGroup.every(groupItem => {
                              const itemTotal = Number(groupItem.price) * Number(groupItem.quantity);
                              const paidAmount = Number(groupItem.paidAmount) || 0;
                              return paidAmount >= itemTotal;
                            });
                          })()}
                          onChange={() => {
                            // Don't allow changing selection for fully paid items
                            const itemsInGroup = group.items.filter(groupItem => item.ids.includes(groupItem.id));
                            const allItemsPaidOrInCart = itemsInGroup.every(groupItem => {
                              // Check if item is in cart
                              if (groupItem.status === 'added_to_cart') {
                                return true;
                              }
                              
                              const itemTotal = Number(groupItem.price) * Number(groupItem.quantity);
                              const paidAmount = Number(groupItem.paidAmount) || 0;
                              return paidAmount >= itemTotal;
                            });
                            
                            if (allItemsPaidOrInCart) return;
                            
                            setSelectedItems(prev => {
                              const newSelected = new Set(prev);
                              if (allSubItemsSelected) {
                                item.ids.forEach(id => newSelected.delete(id));
                              } else {
                                item.ids.forEach(id => newSelected.add(id));
                              }
                              return newSelected;
                            });
                          }}
                          disabled={(() => {
                            // Disable if all items in this group are fully paid
                            const itemsInGroup = group.items.filter(groupItem => item.ids.includes(groupItem.id));
                            return itemsInGroup.every(groupItem => {
                              // Disable if item is in cart
                              if (groupItem.status === 'added_to_cart') {
                                return true;
                              }
                              
                              const itemTotal = Number(groupItem.price) * Number(groupItem.quantity);
                              const paidAmount = Number(groupItem.paidAmount) || 0;
                              return paidAmount >= itemTotal;
                            });
                          })()}
                          sx={{ 
                            mt: 1,
                            '&.Mui-disabled': {
                              color: 'rgba(0, 0, 0, 0.26)'
                            }
                          }}
                        />
                        <Box sx={{
                          width: 64,
                          height: 64,
                          mr: 2,
                          borderRadius: '6px',
                          overflow: 'hidden',
                          flexShrink: 0,
                          bgcolor: '#e0e0e0'
                        }}>
                          <img
                            src={item.imageUrl || `https://via.placeholder.com/64x64?text=${encodeURIComponent(item.name.substring(0, 10))}`}
                            alt={item.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography noWrap sx={{ fontWeight: '500' }}>
                              {item.name}
                            </Typography>
                            <Typography sx={{ fontWeight: '500', ml: 1, flexShrink: 0 }}>
                              (x{item.quantity})
                            </Typography>
                          </Box>
                          {item.displayOptions && item.displayOptions.map((opt, optIndex) => (
                            <Typography key={optIndex} variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.3, mt: 0.25 }}>
                              {opt}
                            </Typography>
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1 }}>
                          <Typography sx={{ fontWeight: '500', minWidth: 'max-content' }}>
                            ₡{(item.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </Typography>
                          {item.status && (
                            <Chip
                              label={getStatusLabel(item.status)}
                              size="small"
                              color={getStatusChipColor(item.status)}
                              sx={{ mt: 0.5, height: '20px', fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Box>

      <Box ref={footerRef} sx={{
        p: 2,
        borderTop: '1px solid #e0e0e0',
        bgcolor: '#ffffff',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        position: 'fixed', // Changed from sticky to fixed
          bottom: 0,
          left: 0,
          right: 0,
        zIndex: 10
      }}>
        <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
        <Button
            variant="outlined"
          fullWidth
            sx={{
              mb: 1.5,
              borderRadius: '8px',
              py: 0.75,
              borderColor: 'primary.main',
              color: 'primary.main',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              '&:hover': {
                backgroundColor: 'primary.light',
                borderColor: 'primary.dark',
              }
            }}
            onClick={() => {
              if (order?.table?.qrCode) {
                // Track user clicking to add more items
                trackWithUserContext(
                  'add_more_items_clicked',
                  'engagement',
                  'ready_to_pay_page',
                  selectedTotal,
                  {
                    current_total: selectedTotal,
                    order_id: order.id,
                    from_payment_flow: true
                  },
                  currentUser,
                  order.table?.id,
                  restaurantId
                );
                navigate(`/table/qr/${order.table.qrCode}?modifyOrder=true`);
              }
            }}
            disabled={payNowLoading || !order?.table?.qrCode}
          >
            Agregar Más Artículos
        </Button>
          {order && (
            <>
              {order.restaurant?.enablePayments !== false ? (
                <PayNowButton
                  orderId={order.id}
                  currency={order.currency || 'USD'}
                  restaurantId={order.restaurantId || ''}
                  loading={payNowLoading}
                  setLoading={setPayNowLoading}
                  amount={selectedTotal}
                  userId={currentUserId}
                  itemsToPay={itemsToPay}
                  onPaymentInitiated={() => {
                    // Track payment button click with social payment analysis
                    const allItems = Array.isArray(order?.items) ? order.items : [];
                    const myItems = allItems.filter((item: any) => item.user?.id === currentUserId);
                    const othersItems = allItems.filter((item: any) => item.user?.id !== currentUserId);
                    const selfAmount = myItems.reduce((sum: number, item: any) => sum + ((item.price || 0) * item.quantity), 0);
                    const othersAmount = othersItems.reduce((sum: number, item: any) => sum + ((item.price || 0) * item.quantity), 0);
                    
                    const payingForUsers = othersItems.map((item: any) => ({
                      id: item.user?.id || 'unknown',
                      name: item.user?.name || 'Unknown User',
                      amount: (item.price || 0) * item.quantity
                    }));

                    trackPaymentButtonClick(
                      {
                        orderId: order.id,
                        totalAmount: selectedTotal,
                        selfAmount: selfAmount,
                        othersAmount: othersAmount,
                        payingForUsers: payingForUsers
                      },
                      currentUser,
                      order.table?.id,
                      restaurantId
                    );
                    
                    // Increment table payment counter
                    if (order.table?.id) {
                      incrementTablePayment(order.table.id, restaurantId);
                    }
                  }}
                  sx={{
                    width: '100%',
                    borderRadius: '8px',
                    py: 0.75,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                />
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  disabled={payNowLoading}
                  onClick={async () => {
                    setPayNowLoading(true);
                    try {
                      // Call the notify ready to pay endpoint
                      await api.post(`/orders/${order.id}/notify-ready-to-pay`);
                      
                      // Show success message
                      alert('Se ha notificado al mesero que la mesa está lista para pagar. El mesero se acercará a su mesa.');
                      
                      // Track the action
                      trackWithUserContext(
                        'notify_ready_to_pay_clicked',
                        'payment_flow',
                        'ready_to_pay_page',
                        selectedTotal,
                        {
                          order_id: order.id,
                          total_amount: selectedTotal,
                          payments_disabled: true
                        },
                        currentUser,
                        order.table?.id,
                        restaurantId
                      );
                    } catch (error) {
                      console.error('Error notifying ready to pay:', error);
                      alert('Error al notificar. Por favor intente nuevamente.');
                    } finally {
                      setPayNowLoading(false);
                    }
                  }}
                  sx={{
                    width: '100%',
                    borderRadius: '8px',
                    py: 0.75,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#4CAF50',
                    '&:hover': {
                      backgroundColor: '#45a049',
                    },
                  }}
                >
                  {payNowLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Llamar al Mesero para Pagar'
                  )}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Payment Error Modal */}
              {/* Payment error modal is now handled by Redux and rendered globally in App.tsx */}
    </Box>
  );
};

export default ReadyToPay;